import { Dataset, DtFilter, DtSort } from '@/components/data-table/helpers';
import {
  CreateOrgModel,
  OrgCreateFormValues,
  OrgModel,
  OrgPolicyEngine,
  OrgStatus,
} from '../models/org.model';
import {
  CreateResponse,
  DEFAULT_SERVICE_ERROR,
  DeleteResponse,
  FieldError,
  getProblemMessage,
  PatchResponse,
  SAFE_LIMIT,
} from '.';
import { orderBy } from 'lodash';
import { DateFormat, downloadCsv, getNiceDate, JsonObject } from '../helpers';
import { create, deleteApi, get, patch } from './sp-api.service';
import { AxiosError } from 'axios';

type ServiceFieldError = {
  field: string;
  rule: string;
};

export const getOrgs = async (
  page: number,
  pageSize: number,
  dtSort?: DtSort,
  dtFilter?: DtFilter
): Promise<Dataset<OrgModel>> => {
  const response = await get('/orgs', {
    fields:
      'id,name,domains,status,mode,partnerId,allowSupportAccess,contact,disablePromptRetention',
    page: 1,
    pageSize: SAFE_LIMIT,
  });

  if (!response) {
    return {
      page,
      pageSize,
      rows: [],
      total: 0,
    };
  }

  let rows = (response as JsonObject[]).map((data) => new OrgModel(data));

  // Filter
  if (dtFilter) {
    const { keyword, values } = dtFilter;

    if (keyword) {
      const safeKeyword = String(keyword).trim().toLowerCase();
      rows = rows.filter(({ name, domains }) => {
        if (String(name).toLowerCase().includes(safeKeyword)) {
          return true;
        }

        if (domains.some((domain) => String(domain).toLowerCase().includes(safeKeyword))) {
          return true;
        }

        return false;
      });
    }

    values.forEach(({ columnName, value }) => {
      switch (columnName) {
        case 'status': {
          rows = rows.filter(({ status }) => (value as OrgStatus[]).includes(status));
          break;
        }
        case 'partnerId': {
          rows = rows.filter(({ partnerId }) => (value as string[]).includes(partnerId));
          break;
        }
      }
    });
  }

  // Sort
  if (dtSort) {
    const { columnName, direction } = dtSort;
    rows = orderBy(
      rows,
      [(org: OrgModel) => String(org[columnName as keyof OrgModel]).toLowerCase()],
      [direction]
    );
  }

  // Paginate
  const startIndex = page * pageSize;
  const total = rows.length;
  rows = rows.slice(startIndex, startIndex + pageSize);

  return Promise.resolve({
    page,
    pageSize,
    rows,
    total,
  });
};

export const getOrgsByPartnerId = async (partnerId: string): Promise<OrgModel[]> => {
  const filter = {
    values: [{ columnName: 'partnerId', value: [partnerId] }],
    keyword: '',
    fields: [],
  };
  const { rows } = await getOrgs(0, SAFE_LIMIT, undefined, filter);

  return rows;
};

export const getOrgById = async (orgId: string): Promise<OrgModel | null> => {
  const response = await get(`/orgs/${orgId}`);
  return response ? new OrgModel(response as JsonObject) : null;
};

export const patchOrg = async (
  orgId: string,
  org: OrgModel,
  isSuper: boolean
): Promise<PatchResponse> => {
  const createOrgModel = new CreateOrgModel(org, false, isSuper);
  return patch(`/orgs/${orgId}`, createOrgModel._props, handlePatchErrorResponse);
};

// Root only. The body must hold only `policyEngine`, with both `version` and `mergeStrategy`.
export const patchOrgPolicyEngine = async (
  orgId: string,
  { version, mergeStrategy }: OrgPolicyEngine
): Promise<PatchResponse> => {
  return patch(
    `/orgs/${orgId}`,
    { policyEngine: { version, mergeStrategy } },
    handlePolicyEnginePatchErrorResponse
  );
};

export const deleteOrg = async (orgId: string): Promise<DeleteResponse> => {
  return deleteApi(`/orgs/${orgId}`);
};

export const createOrg = async (
  org: OrgCreateFormValues,
  isSuper: boolean
): Promise<CreateResponse> => {
  const createOrgModel = new CreateOrgModel(org, true, isSuper);

  return create('/orgs', createOrgModel._props, handleCreateErrorResponse).then((response) => {
    if (response.data) {
      response.model = new OrgModel(response.data);
    }

    return response;
  });
};

export const downloadOrgs = async (sort?: DtSort, filter?: DtFilter): Promise<boolean> => {
  const allOrgs = await getOrgs(0, SAFE_LIMIT, sort, filter);

  const csvRows = allOrgs.rows.map(
    ({ name, domains, status, mode, contact, disablePromptRetention }) => {
      const { name: contactName, email = '' } = contact || {};

      return [
        name,
        domains.join(', '),
        status,
        mode,
        contactName,
        email,
        disablePromptRetention ? 'yes' : 'no',
      ];
    }
  );

  const headers = [
    'Organization Name',
    'Domains',
    'Status',
    'Mode',
    'Contact Name',
    'Contact Email',
    'Disable Prompt Retention',
  ];

  downloadCsv(
    [headers, ...csvRows],
    `f5-workforce-ai-security-orgs-${getNiceDate(new Date(), DateFormat.NOTIME).formatted}.csv`
  );

  return true;
};

// Normalize the org service write error response to the expected format
const handleCreateErrorResponse = (error: AxiosError): CreateResponse => {
  if (!error.response) {
    return { created: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 403) {
    const { detail } = data || {};
    return { created: false, error: (detail as string) || DEFAULT_SERVICE_ERROR };
  }

  if (status !== 400 || !Array.isArray(data.errors)) {
    return { created: false, error: DEFAULT_SERVICE_ERROR };
  }

  return { created: false, fieldErrors: getFieldErrors(data.errors as ServiceFieldError[]) };
};

const handlePatchErrorResponse = (error: AxiosError): PatchResponse => {
  if (!error.response) {
    return { patched: false, error: DEFAULT_SERVICE_ERROR };
  }

  const { status, data } = error.response as { status: number; data: JsonObject };

  if (status === 403) {
    const { detail } = data || {};
    return { patched: false, error: (detail as string) || DEFAULT_SERVICE_ERROR };
  }

  if (status !== 400 || !Array.isArray(data.errors)) {
    return { patched: false, error: DEFAULT_SERVICE_ERROR };
  }

  return { patched: false, fieldErrors: getFieldErrors(data.errors as ServiceFieldError[]) };
};

const handlePolicyEnginePatchErrorResponse = (error: AxiosError): PatchResponse => {
  const fallbacks: Record<number, string> = {
    403: 'Only a root user can change the policy engine',
    422: 'The organization cannot use this policy engine setting',
  };

  return {
    patched: false,
    error: getProblemMessage(error, fallbacks[error.response?.status || 0]),
  };
};

const getFieldErrors = (errors: ServiceFieldError[]): FieldError[] => {
  return errors.map(({ field, rule }) => ({
    msg: rule,
    path: field,
    value: '',
  }));
};
